import type { AuthUIConfig, AuthUIProviderConfig } from './ui';

export type AuthConfigIssueLevel = 'warning' | 'error';

export interface AuthConfigIssue {
  level: AuthConfigIssueLevel;
  path: string;
  message: string;
}

const isAbsoluteUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return Boolean(url.protocol && url.host);
  } catch {
    return false;
  }
};

const pushIssue = (
  issues: AuthConfigIssue[],
  level: AuthConfigIssueLevel,
  path: string,
  message: string
) => {
  issues.push({ level, path, message });
};

const validateRoutes = (issues: AuthConfigIssue[], routes?: AuthUIConfig['routes']) => {
  if (!routes) return;

  Object.entries(routes).forEach(([key, value]) => {
    if (!value) {
      pushIssue(issues, 'error', `routes.${key}`, 'Route is empty.');
      return;
    }

    if (!value.startsWith('/')) {
      pushIssue(issues, 'warning', `routes.${key}`, 'Routes should start with a leading slash (e.g. /auth/login).');
    }
  });
};

const validateRedirects = (issues: AuthConfigIssue[], redirects?: AuthUIConfig['redirects']) => {
  if (!redirects) return;

  Object.entries(redirects).forEach(([key, value]) => {
    if (!value) return;

    if (!isAbsoluteUrl(value)) {
      pushIssue(issues, 'warning', `redirects.${key}`, 'Redirects should be absolute URLs for Supabase auth flows.');
    }
  });
};

const validateProviders = (issues: AuthConfigIssue[], providers?: AuthUIProviderConfig[]) => {
  if (!providers || providers.length === 0) return;

  const seen = new Set<string>();

  providers.forEach((provider, index) => {
    if (!provider?.id) {
      pushIssue(issues, 'error', `providers[${index}].id`, 'Provider id is required.');
      return;
    }

    if (seen.has(provider.id)) {
      pushIssue(issues, 'warning', `providers[${index}].id`, `Duplicate provider id "${provider.id}".`);
    }

    seen.add(provider.id);

    if (provider.redirectTo && !isAbsoluteUrl(provider.redirectTo)) {
      pushIssue(issues, 'warning', `providers[${index}].redirectTo`, 'Provider redirect should be an absolute URL.');
    }
  });
};

const validateLayout = (issues: AuthConfigIssue[], layout?: AuthUIConfig['layout']) => {
  if (!layout?.variant) return;

  const allowed = ['split', 'stacked', 'embedded'];
  if (!allowed.includes(layout.variant)) {
    pushIssue(issues, 'error', 'layout.variant', `Invalid layout variant "${layout.variant}".`);
  }
};

export const validateAuthUIConfig = (config?: AuthUIConfig): AuthConfigIssue[] => {
  if (!config) return [];

  const issues: AuthConfigIssue[] = [];

  validateRoutes(issues, config.routes);
  validateRedirects(issues, config.redirects);
  validateProviders(issues, config.providers);
  validateLayout(issues, config.layout);

  return issues;
};

export const formatAuthConfigIssues = (issues: AuthConfigIssue[]): string[] =>
  issues.map((issue) => `[${issue.level.toUpperCase()}] ${issue.path}: ${issue.message}`);
